
import { trackTikTokEvent } from './tiktokPixel';

/**
 * Facebook Pixel Standard Events Reference:
 * - PageView: Track page views (auto-tracked)
 * - ViewContent: Track product/content views
 * - Search: Track search queries
 * - AddToCart: Track when items added to cart
 * - AddToWishlist: Track when items added to wishlist
 * - InitiateCheckout: Track checkout initiations
 * - AddPaymentInfo: Track payment info submissions
 * - Purchase: Track completed purchases
 * - Lead: Track leads/sign-ups
 * - CompleteRegistration: Track registration completions
 */

/**
 * Safely track Facebook Pixel events with retry mechanism.
 * Ensures events are fired even if the Pixel script loads slightly after the component.
 */
export const trackFacebookEvent = (eventName: string, data?: Record<string, any>): Promise<void> => {
  const MAX_RETRIES = 3; // Quick check — if pixel isn't loaded after 300ms, it's not configured
  let attempts = 0;

  // Add deterministic event_id for Purchase deduplication between browser pixel and server CAPI/Events API
  const eventId = data?.order_id
    ? `Purchase_${data.order_id}`
    : (data?.event_id || `${eventName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const eventData = { ...data, event_id: eventId };

  // Guard: Avoid firing ViewContent without content_id (prevents TikTok "Content ID is missing" critical warning)
  const contentIdsList = data?.content_ids;
  const contentsList = data?.contents;
  const hasContentId = Boolean(
    data?.content_id ||
    (Array.isArray(contentIdsList) && contentIdsList.length > 0) ||
    (Array.isArray(contentsList) && contentsList.length > 0)
  );

  if (eventName === 'ViewContent' && !hasContentId) {
    // PageView already tracks generic page visits. Do not track empty ViewContent without product IDs.
    return Promise.resolve();
  }

  // Map Facebook event name to TikTok event name
  // Note: Modern TikTok Events API & Pixel use 'Purchase' as the standard event.
  let ttEventName = eventName;
  if (eventName === 'Purchase') {
    ttEventName = 'Purchase';
  }
  
  // Map Facebook data structure to TikTok data structure with required top-level content_id
  const ttData: any = { ...data };
  
  // Extract primary content ID for TikTok's top-level content_id requirement
  const idFormat = typeof window !== 'undefined' ? localStorage.getItem('tt_catalog_id_format') : 'id';
  let primaryId = data?.content_id;
  if (idFormat === 'sku' && data?.sku) {
    primaryId = data.sku;
  }
  if (!primaryId && Array.isArray(contentIdsList) && contentIdsList.length > 0) {
    primaryId = contentIdsList[0];
  }
  if (primaryId) {
    ttData.content_id = String(primaryId);
    ttData.content_type = data?.content_type || 'product';
  }

  if (Array.isArray(contentIdsList) && contentIdsList.length > 0) {
    const quantity = data?.num_items || 1;
    const contentType = data?.content_type || 'product';
    const contentName = data?.content_name || '';
    const itemPrice = data?.value ? (data.value / quantity) : (data?.price || 0);
    ttData.contents = contentIdsList.map((id: string) => ({
      content_id: String(id),
      content_type: contentType,
      content_name: contentName,
      price: itemPrice,
      quantity: quantity
    }));
  }
  if (data?.search_string) ttData.query = data.search_string;

  // Track TikTok event concurrently
  trackTikTokEvent(ttEventName, ttData, { event_id: eventId }).catch(e => console.error(e));

  // If purchase, also fire CompletePayment for legacy campaign compatibility
  if (eventName === 'Purchase') {
    trackTikTokEvent('CompletePayment', ttData, { event_id: `${eventId}_cp` }).catch(e => console.error(e));
  }

  return new Promise((resolve) => {
    const track = () => {
      if (window.fbq) {
        window.fbq('track', eventName, eventData);
        resolve();
      } else if (attempts < MAX_RETRIES) {
        attempts++;
        setTimeout(track, 100);
      } else {
        // FB Pixel not configured — silently resolve
        resolve();
      }
    };

    track();
  });
};

/**
 * Track a custom event (non-standard Facebook event)
 */
export const trackCustomEvent = (eventName: string, data?: Record<string, any>): Promise<void> => {
  const MAX_RETRIES = 3;
  let attempts = 0;

  return new Promise((resolve) => {
    const track = () => {
      if (window.fbq) {
        const eventData = {
          ...data,
          event_id: `${eventName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        };
        
        window.fbq('trackCustom', eventName, eventData);
        resolve();
      } else if (attempts < MAX_RETRIES) {
        attempts++;
        setTimeout(track, 100);
      } else {
        // FB Pixel not configured — silently resolve
        resolve();
      }
    };

    track();
  });
};

/**
 * Predefined event helpers for common e-commerce actions
 */
export const fbEvents = {
  viewContent: (product: {
    id: string | number;
    name: string;
    price: number;
    category?: string;
  }) => trackFacebookEvent('ViewContent', {
    content_name: product.name,
    content_ids: [product.id.toString()],
    content_type: 'product',
    value: product.price,
    currency: 'BDT',
    content_category: product.category || ''
  }),

  addToCart: (product: {
    id: string | number;
    name: string;
    price: number;
    quantity?: number;
  }) => trackFacebookEvent('AddToCart', {
    content_name: product.name,
    content_ids: [product.id.toString()],
    content_type: 'product',
    value: product.price * (product.quantity || 1),
    currency: 'BDT',
    num_items: product.quantity || 1
  }),

  addToWishlist: (product: {
    id: string | number;
    name: string;
    price: number;
  }) => trackFacebookEvent('AddToWishlist', {
    content_name: product.name,
    content_ids: [product.id.toString()],
    content_type: 'product',
    value: product.price,
    currency: 'BDT'
  }),

  initiateCheckout: (items: Array<{
    id: string | number;
    quantity: number;
  }>, totalValue: number) => trackFacebookEvent('InitiateCheckout', {
    content_ids: items.map(item => item.id.toString()),
    content_type: 'product',
    value: totalValue,
    currency: 'BDT',
    num_items: items.reduce((acc, item) => acc + item.quantity, 0)
  }),

  purchase: (order: {
    orderId: string;
    items: Array<{ id: string | number; quantity: number }>;
    totalValue: number;
  }) => trackFacebookEvent('Purchase', {
    content_ids: order.items.map(item => item.id.toString()),
    content_type: 'product',
    value: order.totalValue,
    currency: 'BDT',
    num_items: order.items.reduce((acc, item) => acc + item.quantity, 0),
    order_id: order.orderId
  }),

  search: (searchQuery: string) => trackFacebookEvent('Search', {
    search_string: searchQuery,
    content_type: 'product'
  })
};
